import React, { useState, useRef, useCallback, useEffect } from "react";
import MainLayout from "../../Layout/mainLayout";
import { ReactComponent as Location } from "../../Global Icons/location.svg";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import TextInput from "../../Global Functions/textInput";
import { updateLocality, viewLocality } from "../../Redux/Actions/localityAction";
import { viewAllCity } from "../../Redux/Actions/settingsActions";
import { useNavigate, useParams } from "react-router-dom";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import "./addLocality.css";

const MAX_NAME_LENGTH = 100;
const MAX_ADDRESS_LENGTH = 200;

const mapContainerStyle = {
    width: "100%",
    height: "400px",
    marginTop: "10px",
    borderRadius: "8px",
};

const defaultCenter = {
    lat: 28.6139,
    lng: 77.209,
};

const libraries = ["places"];

const EditLocality = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const { citiesList = { data: [] } } = useSelector((state) => state.city || {});
    const { locality, singleLoading } = useSelector((state) => state.locality || {});

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries: libraries,
    });

    const [formState, setFormState] = useState({
        city: "",
        name: "",
        place_id: "",
        location: {
            type: "Point",
            coordinates: [0, 0],
        },
        formatted_address: "",
        short_address: "",
        google_url: "",
    });

    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const hasFetchedCities = useRef(false);
    const [markerPosition, setMarkerPosition] = useState(null);
    const [isDataLoaded, setIsDataLoaded] = useState(false);

    const mapRef = useRef(null);
    const autocompleteRef = useRef(null);

    useEffect(() => {
        dispatch({ type: "RESET_LOCALITY" });
        setIsDataLoaded(false);

        if (id) {
            dispatch(viewLocality(id));
        }

        return () => {
            dispatch({ type: "RESET_LOCALITY" });
        };
    }, [id, dispatch]);

    useEffect(() => {
        if (!citiesList?.data?.length && !hasFetchedCities.current) {
            dispatch(viewAllCity());
            hasFetchedCities.current = true;
        }
    }, [dispatch, citiesList?.data?.length]);

    useEffect(() => {
        if (locality && locality.data && !isDataLoaded) {
            const localityData = locality.data;

            const coordinates = localityData.location?.coordinates || [0, 0];
            const lat = coordinates[1] || 0;
            const lng = coordinates[0] || 0;

            setFormState({
                city: localityData.city?._id || localityData.city || "",
                name: localityData.name || "",
                place_id: localityData.place_id || "",
                location: {
                    type: "Point",
                    coordinates: coordinates,
                },
                formatted_address: localityData.formatted_address || "",
                short_address: localityData.short_address || "",
                google_url: localityData.google_url || `https://maps.google.com/?q=${lat},${lng}`,
            });

            if (lat !== 0 && lng !== 0) {
                setMapCenter({ lat, lng });
                setMarkerPosition({ lat, lng });
            }

            setIsDataLoaded(true);
        }
    }, [locality, isDataLoaded]);

    const getCharCountClass = (length, max) => {
        if (length >= max) return "char-count error";
        if (length >= max * 0.8) return "char-count warning";
        return "char-count safe";
    };

    const getCharCountPercentage = (length, max) => {
        return Math.min((length / max) * 100, 100);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        if (name === "name" && value.length > MAX_NAME_LENGTH) return;

        setFormState((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCoordinateChange = (index, value) => {
        const coordinates = [...formState.location.coordinates];
        coordinates[index] = parseFloat(value) || 0;
        setFormState((prev) => ({
            ...prev,
            location: { ...prev.location, coordinates },
        }));

        const newLng = coordinates[0];
        const newLat = coordinates[1];
        setMarkerPosition({ lat: newLat, lng: newLng });
        setMapCenter({ lat: newLat, lng: newLng });
    };

    const onMapClick = useCallback((event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();

        setMarkerPosition({ lat, lng });
        setFormState((prev) => ({
            ...prev,
            location: {
                ...prev.location,
                coordinates: [lng, lat],
            },
            google_url: `https://maps.google.com/?q=${lat},${lng}`,
        }));

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) {
                setFormState((prev) => ({
                    ...prev,
                    place_id: results[0].place_id,
                    formatted_address: results[0].formatted_address,
                    short_address:
                        results[0].address_components[0]?.long_name +
                        ", " +
                        results[0].address_components[1]?.long_name,
                }));
            }
        });
    }, []);

    const onLoad = useCallback((map) => {
        mapRef.current = map;
    }, []);

    const onPlaceChanged = () => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();

            if (place.geometry) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();

                setMarkerPosition({ lat, lng });
                setMapCenter({ lat, lng });

                let shortAddress = "";
                if (place.name && place.vicinity) {
                    shortAddress = `${place.name}, ${place.vicinity}`;
                } else if (place.address_components && place.address_components.length >= 2) {
                    shortAddress = `${place.address_components[0]?.long_name}, ${place.address_components[1]?.long_name}`;
                } else {
                    shortAddress = place.name || place.formatted_address || "";
                }

                setFormState((prev) => ({
                    ...prev,
                    name: place.name || "",
                    place_id: place.place_id || "",
                    location: {
                        ...prev.location,
                        coordinates: [lng, lat],
                    },
                    formatted_address: place.formatted_address || "",
                    short_address: shortAddress,
                    google_url: `https://maps.google.com/?q=${lat},${lng}`,
                }));
            }
        }
    };

    const onAutocompleteLoad = (autocomplete) => {
        autocompleteRef.current = autocomplete;
    };

    const handleSubmit = async () => {
        if (
            !formState.city ||
            !formState.name ||
            !formState.place_id ||
            !formState.formatted_address ||
            !formState.short_address
        ) {
            return toast.error("Please fill all required fields.");
        }

        if (formState.name.length > MAX_NAME_LENGTH) {
            return toast.error(`Name cannot exceed ${MAX_NAME_LENGTH} characters.`);
        }

        if (formState.formatted_address.length > MAX_ADDRESS_LENGTH) {
            return toast.error(
                `Formatted address cannot exceed ${MAX_ADDRESS_LENGTH} characters.`
            );
        }

        if (formState.short_address.length > MAX_ADDRESS_LENGTH) {
            return toast.error(
                `Short address cannot exceed ${MAX_ADDRESS_LENGTH} characters.`
            );
        }

        if (
            formState.location.coordinates[0] === 0 &&
            formState.location.coordinates[1] === 0
        ) {
            return toast.error("Please select a location on the map.");
        }

        const payload = {
            city: formState.city,
            name: formState.name,
            place_id: formState.place_id,
            location: formState.location,
            formatted_address: formState.formatted_address,
            short_address: formState.short_address,
            google_url: formState.google_url,
        };

        try {
            const response = await dispatch(updateLocality(id, payload));
            console.log("API data:", response);

            if (
                response &&
                (response.statusCode === 200 || response.status === "success")
            ) {
                toast.success("Locality updated successfully!");
                navigate("/locality");
            } else {
                toast.error(response.message || "Failed to update locality");
            }
        } catch (error) {
            toast.error("Error updating locality");
        }
    };

    const CharCountDisplay = ({ current, max, fieldName }) => {
        const percentage = getCharCountPercentage(current, max);
        const statusClass = getCharCountClass(current, max);
        const isWarning = current >= max * 0.8 && current < max;
        const isError = current >= max;

        return (
            <div className={`char-count-container ${statusClass}`}>
                <div className="char-count-bar">
                    <div
                        className="char-count-fill"
                        style={{
                            width: `${percentage}%`,
                            backgroundColor:
                                isError ? "#d9534f" : isWarning ? "#f0ad4e" : "#5cb85c",
                        }}
                    />
                </div>
                <div className="char-count-text">
                    <span className="char-count-current">{current}</span>
                    <span className="char-count-separator">/</span>
                    <span className="char-count-max">{max}</span>
                    {isWarning && !isError && (
                        <span className="char-count-warning-icon">⚠️</span>
                    )}
                    {isError && <span className="char-count-error-icon">✕</span>}
                </div>
            </div>
        );
    };

    if (singleLoading) {
        return (
            <MainLayout>
                <div className="franchise-form-container">
                    <div className="franchise-form-wrapper franchise-card">
                        <div className="franchise-card-content">
                            <p style={{ textAlign: "center", padding: "20px" }}>
                                Loading locality data...
                            </p>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="franchise-form-container">
                <div className="franchise-form-wrapper franchise-card">
                    <div className="franchise-card-content">
                        <div className="franchise-header">
                            <div className="franchise-header-icon">
                                <Location style={{ height: '30px', width: '30px' }} />
                            </div>
                            <h1>Edit Locality</h1>
                        </div>

                        <div className="form-section">
                            <label className="form-label" htmlFor="city_select">
                                City <span style={{ color: "red" }}>*</span>
                            </label>
                            <select
                                id="city_select"
                                name="city"
                                value={formState.city}
                                onChange={handleChange}
                                className="form-input"
                            >
                                <option value="" disabled>
                                    Select City
                                </option>
                                {citiesList?.data.map((city) => (
                                    <option key={city._id} value={city._id}>
                                        {city.city_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-section">
                            <label className="form-label">
                                Search Location <span style={{ color: "red" }}>*</span>
                            </label>

                            {loadError && (
                                <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    background: '#f8f9fa',
                                    borderRadius: '8px',
                                    height: '432px'
                                }}>
                                    <div style={{ fontSize: '24px', marginBottom: '10px' }}>🗺️</div>
                                    <p style={{ margin: '0 0 10px 0', fontWeight: '500' }}>Map unavailable</p>
                                    <p style={{ fontSize: '14px', color: '#666' }}>Use coordinates below</p>
                                </div>
                            )}

                            {!isLoaded && !loadError && (
                                <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    background: '#f8f9fa',
                                    borderRadius: '8px',
                                    height: '432px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <div style={{ fontSize: '24px', marginBottom: '10px' }}>🗺️</div>
                                    <p style={{ margin: '0 0 10px 0', fontWeight: '500' }}>Loading map...</p>
                                </div>
                            )}

                            {isLoaded && !loadError && (
                                <div>
                                    <Autocomplete
                                        onLoad={onAutocompleteLoad}
                                        onPlaceChanged={onPlaceChanged}
                                    >
                                        <input
                                            type="text"
                                            placeholder="Search for a place..."
                                            className="form-input"
                                            style={{ marginBottom: "10px", width: "100%" }}
                                        />
                                    </Autocomplete>
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        center={mapCenter}
                                        zoom={13}
                                        onClick={onMapClick}
                                        onLoad={onLoad}
                                    >
                                        {markerPosition && <Marker position={markerPosition} />}
                                    </GoogleMap>
                                </div>
                            )}

                            <p style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                                Click on the map to set location or search for a place above
                            </p>
                        </div>

                        <div className="form-section">
                            <TextInput
                                label="Name"
                                name="name"
                                value={formState.name}
                                onChange={handleChange}
                                required
                                inputClass="form-input"
                                labelClass="form-label"
                                maxLength={MAX_NAME_LENGTH}
                            />
                            <CharCountDisplay
                                current={formState.name.length}
                                max={MAX_NAME_LENGTH}
                                fieldName="Name"
                            />
                        </div>

                        {/* Coordinates */}
                        <div
                            className="form-section form-grid-2"
                            style={{ marginTop: "-24px" }}
                        >
                            <div>
                                <label className="form-label" htmlFor="longitude_input">
                                    Longitude <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    style={{ width: "94.2%" }}
                                    id="longitude_input"
                                    name="longitude"
                                    type="number"
                                    step="any"
                                    value={formState.location.coordinates[0]}
                                    onChange={(e) => handleCoordinateChange(0, e.target.value)}
                                    className="form-input"
                                    required
                                />
                            </div>

                            <div>
                                <label className="form-label" htmlFor="latitude_input">
                                    Latitude <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                    style={{ width: "94.2%" }}
                                    id="latitude_input"
                                    name="latitude"
                                    type="number"
                                    step="any"
                                    value={formState.location.coordinates[1]}
                                    onChange={(e) => handleCoordinateChange(1, e.target.value)}
                                    className="form-input"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-section">
                            <TextInput
                                label="Formatted Address"
                                name="formatted_address"
                                value={formState.formatted_address}
                                onChange={handleChange}
                                required
                                inputClass="form-input"
                                labelClass="form-label"
                                maxLength={MAX_ADDRESS_LENGTH}
                                disabled={true}
                            />
                        </div>

                        <div className="form-section">
                            <TextInput
                                label="Short Address"
                                name="short_address"
                                value={formState.short_address}
                                onChange={handleChange}
                                required
                                inputClass="form-input"
                                labelClass="form-label"
                                maxLength={MAX_ADDRESS_LENGTH}
                                disabled={true}
                            />
                        </div>

                        {/* Fixed Form Navigation */}
                        <div className="form-navigation">
                            <button
                                type="button"
                                onClick={() => navigate("/locality")}
                                style={{ marginRight: '10px' }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                            >
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditLocality;